/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { UPDATE_ATTRIBUTE, DELETE_ATTRIBUTE, UPLOAD_FILE } from "../graphql";
import { VApplication } from "../../../application";
import { CREATE_ATTRIBUTE } from "../graphql/createAttribute.mutation";

const is = require("is_js");
const log: any = LoggerModule?.getInstance();

export interface AttributeFormData {
  name?: string;
  isDynamic?: boolean;
  isMandatory?: boolean;
  isLabel?: boolean;
  isVisible?: boolean;
  isSingleVal?: boolean;
  images?: any[];
  presentation?: string;
  section?: string;
  costumeFeature?: string;
  [key: string]: any;
}

export interface UpdateAttributeProps {
  formData: AttributeFormData;
  id: string;
  section?: string;
}

export interface CreateAttributeProps extends AttributeFormData {
  formData?: AttributeFormData;
  id?: string;
}

export interface DeleteAttributeProps {
  id: string;
  costumeTypeId?: string;
}

const ATTRIBUTE_FORM_FIELDS = [
  "name",
  "isDynamic",
  "isMandatory",
  "isLabel",
  "isVisible",
  "isSingleVal",
  "images",
];

const ATTRIBUTE_PARENT_FIELDS = ["presentation", "section", "costumeFeature"];

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const trimIfString = (value: any) =>
  typeof value === "string" ? value.trim() : value;

const normalizeImages = (images: any) => {
  if (!Array.isArray(images)) return images;
  return images.map((image: any) => image?.base64 ?? image).filter(Boolean);
};

const buildAllowedPayload = (args: any, includeParentFields = true) => {
  const source = {
    ...(args?.formData ?? args ?? {}),
  };

  const allowedFields = includeParentFields
    ? [...ATTRIBUTE_FORM_FIELDS, ...ATTRIBUTE_PARENT_FIELDS]
    : ATTRIBUTE_FORM_FIELDS;

  return allowedFields.reduce((payload: any, field) => {
    if (!Object.prototype.hasOwnProperty.call(source, field)) return payload;

    const value = source[field];

    if (value !== undefined) {
      payload[field] =
        field === "name"
          ? trimIfString(value)
          : field === "images"
          ? normalizeImages(value)
          : value;
    }

    return payload;
  }, {});
};

export interface IAttributeMethods extends IPluginMethodMap {
  updateAttribute(args: any): Promise<any>;
  createAttribute(args: any): Promise<any>;
  deleteAttribute(args: any): Promise<any>;
}

export class AttributeService implements IAgentPlugin {
  readonly methods: IAttributeMethods;

  constructor() {
    this.methods = {
      updateAttribute: this.updateAttribute.bind(this),
      createAttribute: this.createAttribute.bind(this),
      deleteAttribute: this.deleteAttribute.bind(this),
    };
  }

  async createAttribute(args: CreateAttributeProps) {
    log?.blue?.(`createAttribute server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const data = buildAllowedPayload(args, true);

        if (Object.prototype.hasOwnProperty.call(data, "images")) {
          data.images = await this.uploadFile(data.images, data);
        }

        const res = await client.mutate({
          mutation: CREATE_ATTRIBUTE,
          variables: { data },
          fetchPolicy: "no-cache",
        });

        if (res?.data) {
          return {
            ...res?.data?.createCostumeAttribute,
          };
        }
      }
    } catch (err: any) {
      const msg = err?.graphQLErrors?.find((item: any) => item.extensions)
        ?.extensions?.display_message;
      throw Error(msg || err?.message);
    }
  }

  async updateAttribute(args: UpdateAttributeProps) {
    log?.blue?.(`updateAttribute server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const update = buildAllowedPayload(args, false);

        if (Object.prototype.hasOwnProperty.call(update, "images")) {
          update.images = await this.uploadFile(update.images, update);
        }

        const res = await client.mutate({
          mutation: UPDATE_ATTRIBUTE,
          variables: {
            filter: { _id: args?.id },
            update,
          },
          fetchPolicy: "no-cache",
        });

        if (res?.data) {
          return {
            ...res?.data?.updateCostumeAttribute,
          };
        }
      }
    } catch (err: any) {
      const msg = err?.graphQLErrors?.find((item: any) => item.extensions)
        ?.extensions?.display_message;
      throw Error(msg || err?.message);
    }
  }

  async deleteAttribute(args: DeleteAttributeProps) {
    log?.blue?.(`deleteAttribute server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.mutate({
          mutation: DELETE_ATTRIBUTE,
          variables: {
            filter: { _id: args?.id },
            costumeTypeId: args?.costumeTypeId,
          },
          fetchPolicy: "no-cache",
        });

        if (res?.data) {
          return {
            ...res?.data,
          };
        }
      }
    } catch (err: any) {
      const msg = err?.graphQLErrors?.find((item: any) => item.extensions)
        ?.extensions?.display_message;
      throw Error(msg || err?.message);
    }
  }

  async uploadFile(arr: any, formData: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const imagesUrlArray: any = [];
    let fileType = "jpeg";

    if (arr && is.not.empty(arr)) {
      for (const obj of arr) {
        if (obj && is.existy(obj)) {
          const dataString = typeof obj === "string" ? obj : obj?.base64 ?? "";
          if (!dataString) continue;
          if (this.isValidHttpUrl(dataString)) {
            imagesUrlArray.push(dataString);
            continue;
          }

          fileType = this.getFileTypeFromBase64(dataString) ?? "jpeg";

          try {
            if (client) {
              const res = await client.mutate({
                mutation: UPLOAD_FILE,
                variables: {
                  name: fileType,
                  data: dataString,
                  category1: "costumeattributes",
                  category2: formData?.name ?? "costumeImg",
                  addBandName: true,
                },
                fetchPolicy: "no-cache",
              });

              if (res?.data?.uploadFile) {
                imagesUrlArray.push(res?.data?.uploadFile);
              }
            }
          } catch (err: any) {
            const _display_message =
              err?.graphQLErrors &&
              Array.isArray(err?.graphQLErrors) &&
              err?.graphQLErrors.find((item: any) => item.extensions);

            if (_display_message?.extensions?.display_message) {
              throw Error(_display_message?.extensions?.display_message);
            }

            throw err;
          }
        }
      }
    }

    return imagesUrlArray;
  }

  private isValidHttpUrl(str: string) {
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    return urlRegex.test(str);
  }

  private getFileTypeFromBase64(base64String: string) {
    const matches = base64String.match(/^data:image\/(\w+)/);
    return matches && matches.length === 2 ? matches[1] : null;
  }
}
